import { elapsedBeforePeriodMs, toElapsedMs } from './clock.js';
import { groupByClockReading, normalizeEvents } from './order.js';
import type { CoreEvent, CoreGameConfig, Stint, Warning } from './types.js';
import { CoreEventType, WarningCode } from './types.js';

/**
 * Lineup stints: the spine of the whole engine.
 *
 * Minutes, plus-minus, and the fairness meter are all derived from stints, so a
 * bug here is wrong everywhere at once. The rules:
 *
 * - A stint is a continuous span in ONE period with ONE unchanging lineup.
 * - The lineup carries across a period break unless a substitution says
 *   otherwise, which is what actually happens at halftime.
 * - An inconsistency (subbing in a player already on the floor) produces a
 *   warning and the derivation continues. It never throws. A scorekeeper mid
 *   game cannot act on an exception, and BUILD_PLAN 5.3 is explicit that
 *   invariants warn rather than block.
 */

interface OpenStint {
  readonly period: number;
  readonly startElapsedMs: number;
  readonly lineup: readonly string[];
  /** Ids of the non-substitution events recorded while this lineup was on. */
  readonly eventIds: string[];
  /** A sport event was recorded during it, so it happened even at zero length. */
  recorded: boolean;
  /** Opened after its period had already been ended (issue #44 item 4). */
  readonly reopened: boolean;
}

export interface StintResult {
  readonly stints: readonly Stint[];
  readonly warnings: readonly Warning[];
  /** Elapsed game time of the last recorded event. Zero for an empty log. */
  readonly lastElapsedMs: number;
  /**
   * The stint each non-substitution event counts for, by event id. Plus-minus
   * reads this instead of matching on elapsed time; `applyReading` says how a
   * play at a substitution's clock reading is placed.
   */
  readonly stintOf: ReadonlyMap<string, Stint>;
}

const CORE_TYPES: ReadonlySet<string> = new Set(Object.values(CoreEventType));

function sortedLineup(lineup: ReadonlySet<string>): readonly string[] {
  return [...lineup].sort();
}

/** A substitution of one of our players, the only kind that moves a lineup. */
type Substitution = CoreEvent & { readonly playerId: string };

function changesLineup(event: CoreEvent): event is Substitution {
  return (
    event.team === 'us' &&
    event.playerId !== null &&
    (event.type === CoreEventType.SUB_IN || event.type === CoreEventType.SUB_OUT)
  );
}

/**
 * Whether a play's place against a substitution at its own clock reading is
 * known: it is a tapped play (it carries the client's tap id, ADR-0058) and
 * not a correction standing in for an earlier row. The client writes an
 * attribution copy (`meta.replaces`) and an Undo restore (`meta.restores`) at
 * the ORIGINAL's reading with a new clientSeq, so the copy's own sequence
 * says nothing about which side of the substitution the play fell on, and the
 * original it names is absent from the client's log and voided in the
 * server's. A row written before the tap id existed is unplaced for the same
 * reason: an old correction carried no name at all.
 */
function placedBySequence(event: CoreEvent): boolean {
  return (
    typeof event.meta['tap'] === 'string' &&
    typeof event.meta['replaces'] !== 'string' &&
    typeof event.meta['restores'] !== 'string'
  );
}

function lineupKey(lineup: ReadonlySet<string>): string {
  return JSON.stringify(sortedLineup(lineup));
}

export function computeStints(events: readonly CoreEvent[], config: CoreGameConfig): StintResult {
  const structure = config.periods;
  const ordered = normalizeEvents(events);
  const groups = groupByClockReading(ordered);

  const stints: Stint[] = [];
  const warnings: Warning[] = [];
  const stintOf = new Map<string, Stint>();
  // Where each period was ended, so a play that arrives after its PERIOD_END
  // reopens the period from there rather than from its start (issue #44 item
  // 4). Elapsed time only grows within a period in processed order, so the
  // latest end is the one to keep.
  const closedAt = new Map<number, number>();
  let lineup: ReadonlySet<string> = new Set<string>();
  // Asserted rather than annotated: every assignment after this one happens
  // inside a closure, and an annotation would let the compiler narrow `open`
  // to `null` for the rest of the function.
  let open = null as OpenStint | null;
  let lastElapsedMs = 0;
  let previousElapsedMs = 0;

  const openAt = (
    period: number,
    startElapsedMs: number,
    reopened: boolean,
    on: ReadonlySet<string> = lineup,
  ): OpenStint => ({
    period,
    startElapsedMs,
    lineup: sortedLineup(on),
    eventIds: [],
    recorded: false,
    reopened,
  });

  const close = (at: number): void => {
    if (open === null) return;
    // Never negative. Elapsed time only grows within a period in processed
    // order, so this is a floor, not a correction.
    const end = Math.max(at, open.startElapsedMs);
    const duration = end - open.startElapsedMs;
    // A zero-length stint is bookkeeping (the starting five arriving at once)
    // unless something was recorded during it: a substitution at a dead ball
    // followed by scoring at the same reading is a real lineup that took
    // the floor for no game time, and its points are its own.
    if (duration > 0 || (open.recorded && open.lineup.length > 0)) {
      const stint: Stint = {
        period: open.period,
        startElapsedMs: open.startElapsedMs,
        endElapsedMs: end,
        durationMs: duration,
        lineup: open.lineup,
      };
      stints.push(stint);
      for (const id of open.eventIds) stintOf.set(id, stint);
      if (duration > 0 && open.lineup.length !== config.onCourtCount) {
        warnings.push({
          code: WarningCode.LINEUP_COUNT,
          message:
            open.lineup.length === 0
              ? `No players were marked on the floor in period ${String(open.period)}. Minutes for that stretch cannot be credited until a starting lineup is recorded.`
              : `${String(open.lineup.length)} players were on the floor in period ${String(open.period)} instead of ${String(config.onCourtCount)}. Check the substitutions around that stretch.`,
          period: open.period,
          eventId: null,
          meta: { lineupSize: open.lineup.length, expected: config.onCourtCount },
        });
      }
    }
    open = null;
  };

  const ensureOpen = (period: number): OpenStint => {
    if (open === null) {
      const endedAt = closedAt.get(period);
      open =
        endedAt === undefined
          ? openAt(period, elapsedBeforePeriodMs(period, structure), false)
          : openAt(period, endedAt, true);
    }
    return open;
  };

  const record = (stint: OpenStint, event: CoreEvent): void => {
    stint.eventIds.push(event.id);
    if (!CORE_TYPES.has(event.type)) stint.recorded = true;
  };

  /** Apply substitutions in processed order, warning on any that contradict. */
  const applied = (
    from: ReadonlySet<string>,
    subs: readonly Substitution[],
    warn: boolean,
    period: number,
  ): Set<string> => {
    const next = new Set(from);
    for (const event of subs) {
      const playerId = event.playerId;
      if (event.type === CoreEventType.SUB_OUT) {
        if (warn && !next.has(playerId)) {
          warnings.push({
            code: WarningCode.SUB_INCONSISTENT,
            message: `A substitution took a player off the floor who was not recorded as being on it. Minutes for that player may be short.`,
            period,
            eventId: event.id,
            meta: { playerId, direction: 'out' },
          });
        }
        next.delete(playerId);
      } else {
        if (warn && next.has(playerId)) {
          warnings.push({
            code: WarningCode.SUB_INCONSISTENT,
            message: `A substitution put a player on the floor who was already on it. Check the substitutions around that stretch.`,
            period,
            eventId: event.id,
            meta: { playerId, direction: 'in' },
          });
        }
        next.add(playerId);
      }
    }
    return next;
  };

  /**
   * One clock reading. No game time passes inside it, so it can only split
   * lineups, never add minutes.
   *
   * Every substitution at the reading applies to the lineup leaving it. A
   * play at the reading counts for the lineup entering it, as a whole reading
   * did before, unless its place is known: a tapped play (`placedBySequence`)
   * counts after earlier substitutions by clientSeq only when all events at
   * the reading come from one device. Multi-device readings use main's
   * entering-or-opening-leaving rule in full. Devices share no sequence and
   * their wall clocks cannot be trusted against each other.
   */
  const applyReading = (
    period: number,
    elapsed: number,
    subs: readonly Substitution[],
    others: readonly CoreEvent[],
  ): void => {
    const entering = lineup;
    const leaving = applied(entering, subs, true, period);
    // A play whose place is unknown counts for the lineup entering the
    // reading only if that lineup has played time in this stint: at the
    // opening reading of a period (halftime substitutions, then the tip) the
    // entering five have not, and the play belongs to the five leaving it.
    const startedAt =
      open?.startElapsedMs ?? closedAt.get(period) ?? elapsedBeforePeriodMs(period, structure);
    const unplaced = elapsed > startedAt ? entering : leaving;

    const singleDevice = new Set([...subs, ...others].map((event) => event.deviceId)).size === 1;
    const buckets = new Map<string, { on: ReadonlySet<string>; events: CoreEvent[] }>();
    for (const event of others) {
      const on =
        singleDevice && placedBySequence(event)
          ? applied(
              entering,
              subs.filter(
                (sub) => sub.deviceId === event.deviceId && sub.clientSeq < event.clientSeq,
              ),
              false,
              period,
            )
          : unplaced;
      const key = lineupKey(on);
      const bucket = buckets.get(key);
      if (bucket === undefined) buckets.set(key, { on, events: [event] });
      else bucket.events.push(event);
    }

    const enteringKey = lineupKey(entering);
    const leavingKey = lineupKey(leaving);
    const between = [...buckets].filter(([key]) => key !== enteringKey && key !== leavingKey);

    if (enteringKey === leavingKey && between.length === 0) {
      const events = buckets.get(enteringKey)?.events ?? [];
      if (events.length === 0) return;
      const current = ensureOpen(period);
      for (const event of events) record(current, event);
      return;
    }

    const current = ensureOpen(period);
    const reopened = current.reopened;
    for (const event of buckets.get(enteringKey)?.events ?? []) record(current, event);
    close(elapsed);
    for (const [, bucket] of between) {
      const stint = openAt(period, elapsed, reopened, bucket.on);
      open = stint;
      for (const event of bucket.events) record(stint, event);
      close(elapsed);
    }
    lineup = leaving;
    const next = openAt(period, elapsed, reopened);
    open = next;
    if (leavingKey !== enteringKey) {
      for (const event of buckets.get(leavingKey)?.events ?? []) record(next, event);
    }
  };

  for (const group of groups) {
    const period = group.period;
    const elapsed = toElapsedMs(period, group.gameClockMs, structure);
    lastElapsedMs = Math.max(lastElapsedMs, elapsed);

    // Period rollover. Close the previous period's stint at that period's end,
    // not at the current event's elapsed time, so a gap in recording does not
    // leak one period's clock into another's minutes. A period that was
    // reopened by a late play closes at the last reading recorded in it
    // instead: it was already ended once, and the late play is evidence of
    // play up to its own reading, not to the period's length.
    if (open !== null && open.period !== period) {
      close(open.reopened ? previousElapsedMs : elapsedBeforePeriodMs(open.period + 1, structure));
    }
    previousElapsedMs = elapsed;

    const subs = group.events.filter(changesLineup);
    const others = group.events.filter((event) => !changesLineup(event));
    applyReading(period, elapsed, subs, others);

    if (others.some((event) => event.type === CoreEventType.PERIOD_END)) {
      close(elapsed);
      closedAt.set(period, elapsed);
    }
  }

  // The log ended without a PERIOD_END. Close at the last thing we actually
  // saw rather than at the period length: if the scorekeeper stopped recording
  // with four minutes left, those four minutes are not minutes anyone played,
  // and inventing them would inflate every fairness number on the dashboard.
  close(lastElapsedMs);

  return { stints, warnings, lastElapsedMs, stintOf };
}
